import React, { useState, useEffect } from 'react';
import { getFeatures } from '../services/api';
import FeatureGraph from './FeatureGraph';

export default function FeatureExplorer() {
    const [features, setFeatures] = useState([]);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // list, graph, impact
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ category: 'all', priority: 'all' });

    useEffect(() => {
        loadFeatures();
    }, []);

    const loadFeatures = async () => {
        try {
            const res = await getFeatures();
            setFeatures(res.data.features || []);
        } catch (err) {
            console.error('Error loading features:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredFeatures = features.filter(feature => {
        if (filter.category !== 'all' && feature.category !== filter.category) return false;
        if (filter.priority !== 'all' && feature.priority !== filter.priority) return false;
        return true;
    });

    const categories = [...new Set(features.map(f => f.category))];
    const priorities = ['Critical', 'High', 'Medium', 'Low'];

    if (loading) {
        return <div className="animate-pulse p-8">Loading features...</div>;
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-2xl font-bold mb-4">Feature Explorer</h2>
                
                {/* View Mode Tabs */}
                <div className="flex space-x-4 mb-4">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                        List View
                    </button>
                    <button
                        onClick={() => setViewMode('graph')}
                        className={`px-4 py-2 rounded ${viewMode === 'graph' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                        Relationship Graph
                    </button>
                    <button
                        onClick={() => setViewMode('impact')}
                        className={`px-4 py-2 rounded ${viewMode === 'impact' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                        Impact Analysis
                    </button>
                </div>

                {/* Filters */}
                <div className="flex space-x-4">
                    <select
                        value={filter.category}
                        onChange={(e) => setFilter({...filter, category: e.target.value})}
                        className="px-4 py-2 border rounded"
                    >
                        <option value="all">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                    <select
                        value={filter.priority}
                        onChange={(e) => setFilter({...filter, priority: e.target.value})}
                        className="px-4 py-2 border rounded"
                    >
                        <option value="all">All Priorities</option>
                        {priorities.map(priority => (
                            <option key={priority} value={priority}>{priority}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Content based on view mode */}
            {viewMode === 'list' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredFeatures.map(feature => (
                        <FeatureCard
                            key={feature.id}
                            feature={feature}
                            onClick={() => setSelectedFeature(feature)}
                        />
                    ))}
                </div>
            )}

            {viewMode === 'graph' && (
                <FeatureGraph features={filteredFeatures} />
            )}

            {viewMode === 'impact' && (
                <FeatureImpactAnalysis features={filteredFeatures} />
            )}

            {/* Feature Detail Modal */}
            {selectedFeature && (
                <FeatureDetailModal
                    feature={selectedFeature}
                    onClose={() => setSelectedFeature(null)}
                />
            )}
        </div>
    );
}

function FeatureCard({ feature, onClick }) {
    const priorityColors = {
        'Critical': 'bg-red-100 text-red-800',
        'High': 'bg-orange-100 text-orange-800',
        'Medium': 'bg-yellow-100 text-yellow-800',
        'Low': 'bg-green-100 text-green-800'
    };

    const allTables = [
        ...feature.tables.direct,
        ...feature.tables.indirect,
        ...feature.tables.joins
    ];

    return (
        <div
            onClick={onClick}
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
        >
            <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">{feature.name}</h3>
                <span className={`px-2 py-1 rounded text-sm ${priorityColors[feature.priority]}`}>
                    {feature.priority}
                </span>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">{feature.business_description}</p>
            
            <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                    {allTables.length} tables
                </span>
                <span className="bg-gray-100 px-2 py-1 rounded">
                    {feature.category}
                </span>
            </div>
        </div>
    );
}

function FeatureImpactAnalysis({ features }) {
    const [selectedTable, setSelectedTable] = useState(null);
    
    // Create a map of tables to features
    const tableToFeatures = {};
    features.forEach(feature => {
        const allTables = [
            ...(feature.tables?.direct || []),
            ...(feature.tables?.indirect || []),
            ...(feature.tables?.joins || [])
        ];
        
        allTables.forEach(table => {
            if (!tableToFeatures[table]) {
                tableToFeatures[table] = [];
            }
            tableToFeatures[table].push(feature);
        });
    });
    
    const sortedTables = Object.entries(tableToFeatures)
        .sort((a, b) => b[1].length - a[1].length)
        .map(([table, features]) => ({ table, features, count: features.length }));
    
    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Table Impact Analysis</h3>
            <p className="text-sm text-gray-600 mb-4">
                Shows which tables are used by the most features (critical tables)
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <h4 className="font-medium mb-2">Most Used Tables</h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {sortedTables.map(({ table, count }) => (
                            <div
                                key={table}
                                onClick={() => setSelectedTable(table)}
                                className={`p-3 rounded cursor-pointer ${
                                    selectedTable === table 
                                        ? 'bg-blue-100 border border-blue-300' 
                                        : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                            >
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">{table}</span>
                                    <span className={`px-2 py-1 rounded text-sm ${
                                        count > 5 ? 'bg-red-100 text-red-800' :
                                        count > 3 ? 'bg-orange-100 text-orange-800' :
                                        'bg-green-100 text-green-800'
                                    }`}>
                                        {count} features
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                <div>
                    {selectedTable && (
                        <>
                            <h4 className="font-medium mb-2">Features using "{selectedTable}"</h4>
                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                {tableToFeatures[selectedTable].map(feature => (
                                    <div key={feature.id} className="p-3 bg-gray-50 rounded">
                                        <h5 className="font-medium">{feature.name}</h5>
                                        <p className="text-sm text-gray-600 mt-1">
                                            {feature.business_description || 'No description available'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function FeatureDetailModal({ feature, onClose }) {
    if (!feature) return null;
    
    const allTables = [
        ...(feature.tables?.direct || []),
        ...(feature.tables?.indirect || []),
        ...(feature.tables?.joins || [])
    ];
    
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
                <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                        <h3 className="text-lg font-semibold">{feature.name}</h3>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            ✕
                        </button>
                    </div>
                    
                    <div className="px-6 py-4">
                        {/* Business Info */}
                        <div className="mb-6">
                            <h4 className="font-medium mb-2">Business Description</h4>
                            <p className="text-gray-700">{feature.business_description || 'No description available'}</p>
                        </div>
                        
                        {/* User Stories */}
                        {feature.user_stories && feature.user_stories.length > 0 && (
                            <div className="mb-6">
                                <h4 className="font-medium mb-2">User Stories</h4>
                                <ul className="list-disc list-inside space-y-1">
                                    {feature.user_stories.map((story, idx) => (
                                        <li key={idx} className="text-gray-700">{story}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        
                        {/* Technical Details */}
                        <div className="mb-6">
                            <h4 className="font-medium mb-2">Technical Details</h4>
                            <div className="bg-gray-50 rounded p-4">
                                <p><strong>Controller:</strong> {feature.controller}</p>
                                <p><strong>Methods:</strong> {feature.methods?.join(', ')}</p>
                                <p><strong>Models:</strong> {feature.models?.join(', ')}</p>
                            </div>
                        </div>
                        
                        {/* Tables */}
                        <div className="mb-6">
                            <h4 className="font-medium mb-2">Database Tables ({allTables.length})</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {allTables.map(table => (
                                    <div key={table} className="bg-gray-100 rounded px-3 py-1 text-sm">
                                        {table}
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {/* Operations */}
                        {feature.operations && Object.keys(feature.operations).length > 0 && (
                            <div className="mb-6">
                                <h4 className="font-medium mb-2">Operations by Table</h4>
                                <div className="space-y-2">
                                    {Object.entries(feature.operations).map(([table, ops]) => (
                                        <div key={table} className="flex items-center space-x-2">
                                            <span className="font-medium">{table}:</span>
                                            {Array.from(ops).map(op => (
                                                <span key={op} className={`px-2 py-1 rounded text-xs ${
                                                    op === 'create' ? 'bg-green-100 text-green-800' :
                                                    op === 'update' ? 'bg-blue-100 text-blue-800' :
                                                    op === 'delete' ? 'bg-red-100 text-red-800' :
                                                    'bg-gray-100 text-gray-800'
                                                }`}>
                                                    {op}
                                                </span>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}